import { useCallback, useEffect, useMemo, useState } from 'react';
import { AppDataGrid, gridExportMatrix, type AppColumn } from '../../../components/AppDataGrid';
import { AppToolbar } from '../../../components/AppToolbar';
import { FormTitle } from '../../../components/FormTitle';
import { useNotify } from '../../../components/notifications';
import { fmtLocalDate } from '../../../utils/datetime';
import { ALERT_PAGE_CAP, fetchExpirationAlert, type ExpirationAlertRow } from './icmAlertApi';
import '../../backlog/backlog.css';
import './icmAlerts.css';

/**
 * The window, exactly as ALPACA fixes it: `AlertViewControllercs.cs` :53-60
 * computes `DateTime.Now.AddDays(7)` and tests `[ExpiryDate] <= ?` against it.
 * Legacy exposes NO control for this -- 7 is a constant in the controller -- so
 * neither does this page (owner request 2026-10-06, after AI had briefly made it
 * an editable box). Named rather than inlined per §18.4.
 */
const DAYS_AHEAD = 7;

/**
 * Inventory Management → Alert → Expiration Alert.
 *
 * On-shelf lots (LT#) at or past their expiry date.  Ported from ALPACA
 * `Distribution_ListView_Copy_ExpirationAlert` — criteria
 * `([Status]='PendingDispose' OR [Status]='PendingConsume')
 *  And [ExpiryDate] <= now + 7 days`.
 *
 * Read-only, like its two siblings, and the window is FIXED at legacy's 7 days
 * (see `DAYS_AHEAD`). There is deliberately no lower bound: legacy tests only
 * `ExpiryDate <= now + 7`, so everything already expired is listed too -- which
 * is why the count line reads "1,055 lot(s) · 1,054 already expired".
 *
 * `expiryDate` comes from the legacy (Central) `dbo.Distribution`, so it is
 * rendered with the shared `fmtLocalDate` and is NOT `.AsUtc()`-stamped
 * anywhere in the read path (§14b.6).
 */
export default function ExpirationAlertPage() {
  const notify = useNotify();

  const [rows, setRows] = useState<ExpirationAlertRow[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [loadMs, setLoadMs] = useState<number | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [search, setSearch] = useState('');
  const [expiredCount, setExpiredCount] = useState(0);
  const [selected, setSelected] = useState<Set<string | number>>(new Set());

  const load = useCallback(async () => {
    setLoading(true); setError(null);
    const t0 = performance.now();
    try {
      const page = await fetchExpirationAlert(DAYS_AHEAD);
      setRows(page.rows);
      setTotal(page.total);
      setExpiredCount(page.expiredCount);
      setSelected(new Set());
      if (page.total > page.rows.length) {
        notify.info(`Showing the first ${ALERT_PAGE_CAP.toLocaleString()} of ${page.total.toLocaleString()} lots — narrow the search to see the rest.`);
      }
    } catch {
      setError('Failed to load expiration alerts.');
      setRows([]); setTotal(0); setExpiredCount(0);
    } finally {
      setLoadMs(performance.now() - t0);
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => { void load(); }, [load]);

  const filtered = useMemo(() => {
    const t = search.trim().toLowerCase();
    if (!t) return rows;
    return rows.filter(r =>
      (r.lt ?? '').toLowerCase().includes(t) ||
      (r.itemName ?? '').toLowerCase().includes(t) ||
      (r.itemCode ?? '').toLowerCase().includes(t) ||
      (r.description ?? '').toLowerCase().includes(t) ||
      (r.vendorName ?? '').toLowerCase().includes(t)
    );
  }, [rows, search]);

  // Column set + order from the LIVE ALPACA model (BTLIMS.Web/Model.xafml:
  // Distribution_ListView_Copy_ExpirationAlert) — LT# · Item · Description ·
  // Vendor · ExpDate · DaysRemaining · ItemCode.
  const cols: AppColumn<ExpirationAlertRow>[] = [
    { field: 'lt',            header: 'LT#',         width: 130, text: r => r.lt ?? '—', render: r => r.lt ?? '—' },
    { field: 'itemName',      header: 'Item',        width: 240, text: r => r.itemName ?? '—', render: r => r.itemName ?? '—' },
    { field: 'description',   header: 'Description', width: 250, text: r => r.description ?? '—', render: r => r.description ?? '—' },
    { field: 'vendorName',    header: 'Vendor',      width: 150, text: r => r.vendorName ?? '—', render: r => r.vendorName ?? '—' },
    {
      field: 'expiryDate', header: 'ExpDate', width: 120, type: 'date',
      text: r => fmtLocalDate(r.expiryDate) ?? '—',
      render: r => fmtLocalDate(r.expiryDate) ?? '—',
    },
    {
      field: 'daysRemaining', header: 'DaysRemaining', width: 130, type: 'number', align: 'right',
      text: r => String(r.daysRemaining),
      value: r => r.daysRemaining,
      // The one coloured value on these grids: a lot that is already expired.
      render: r => r.daysRemaining < 0
        ? <span className="ica-expired">{r.daysRemaining}</span>
        : <span>{r.daysRemaining}</span>,
    },
    { field: 'itemCode',      header: 'ItemCode',    width: 120, text: r => r.itemCode ?? '—', render: r => r.itemCode ?? '—' },
  ];

  return (
    <div className="bl-page">
      <div className="bl-top">
        <div className="dp-page-hdr">
          <div className="dp-breadcrumb">
            <span>Home</span><i className="ti ti-chevron-right" />
            <span>Inventory Management</span><i className="ti ti-chevron-right" />
            <span>Alert</span><i className="ti ti-chevron-right" />
            <span className="dp-bc-active">Expiration Alert</span>
          </div>
          <div className="dp-title-row">
            <FormTitle pageId="icm-expiration-alert" loadMs={loadMs} loading={loading} />
          </div>
        </div>
      </div>

      <div className="bl-grid-area">
        <div style={{ margin: '0 0 8px' }}>
          <AppToolbar
            onRefresh={load}
            refreshing={loading}
            exportData={{ filename: 'expiration-alert', ...gridExportMatrix(filtered, cols) }}
          />
        </div>
        <div className="list-search-row">
          <div className="list-search">
            <span className="tb-i">🔍</span>
            <input
              type="text"
              value={search}
              onChange={e => setSearch(e.target.value)}
              placeholder="Search LT #, item, code, description, vendor…"
              data-unsaved-ignore
            />
            {search && <span className="list-search-clear" onClick={() => setSearch('')}>×</span>}
          </div>
          <span className="ica-note">
            {total.toLocaleString()} lot(s) · {expiredCount.toLocaleString()} already expired
          </span>
        </div>
        <div className="bl-card bl-card-flex">
          {error ? (
            <div className="bl-err"><i className="ti ti-alert-circle" /> {error}</div>
          ) : (
            <AppDataGrid<ExpirationAlertRow>
              rows={filtered}
              columns={cols}
              getRowId={r => r.oid}
              loading={loading}
              loadMs={loadMs}
              defaultPageSize={20}
              selectable
              selectedIds={selected}
              onSelectionChange={setSelected}
              emptyText="No lots expiring in this window."
            />
          )}
        </div>
      </div>
    </div>
  );
}
